<template>
  <div class="hydration-tracker">
    <div class="tracker-header">
      <h3>Hydration</h3>
      <div v-if="isMatchDay" class="match-day-badge">Match Day</div>
    </div>

    <div class="progress-section">
      <div class="circular-progress">
        <svg viewBox="0 0 100 100">
          <circle
            class="progress-bg"
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#e9ecef"
            stroke-width="8"
          />
          <circle
            class="progress-fill"
            cx="50"
            cy="50"
            r="45"
            fill="none"
            :stroke="progressColor"
            stroke-width="8"
            stroke-linecap="round"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="progressOffset"
          />
        </svg>
        <div class="progress-content">
          <span class="progress-value">{{ percentage }}%</span>
          <span class="progress-label">{{ currentIntake }}ml</span>
          <span class="progress-target">/ {{ dailyTarget }}ml</span>
        </div>
      </div>

      <div v-if="behindSchedule" class="behind-alert">
        <span class="alert-icon">!</span>
        <span>You're behind schedule</span>
      </div>
    </div>

    <div class="quick-add-section">
      <h4>Quick Add</h4>
      <div class="quick-buttons">
        <button
          v-for="portion in quickPortions"
          :key="portion.ml"
          class="quick-btn"
          @click="quickAdd(portion.ml)"
          :disabled="addingHydration"
        >
          <span class="portion-amount">{{ portion.ml }}ml</span>
          <span class="portion-label">{{ portion.label }}</span>
        </button>
      </div>
    </div>

    <div class="drink-type-section">
      <h4>Drink Type</h4>
      <div class="drink-types">
        <button
          v-for="drink in drinkTypes"
          :key="drink.value"
          class="drink-btn"
          :class="{ active: selectedDrink === drink.value }"
          @click="selectedDrink = drink.value"
        >
          <span class="drink-icon" :style="{ backgroundColor: drink.color }">
            {{ drink.symbol }}
          </span>
          <span class="drink-label">{{ drink.label }}</span>
        </button>
      </div>
    </div>

    <div v-if="isMatchDay" class="match-day-targets">
      <h4>Match Day Targets</h4>
      <div class="target-cards">
        <div class="target-card">
          <span class="target-label">Pre-Match</span>
          <span class="target-value">{{ targets.pre_match_ml }}ml</span>
          <div class="target-bar">
            <div
              class="target-fill"
              :style="{ width: preMatchProgress + '%' }"
            ></div>
          </div>
        </div>
        <div class="target-card">
          <span class="target-label">During</span>
          <span class="target-value">{{ targets.during_ml }}ml</span>
          <div class="target-bar">
            <div
              class="target-fill"
              :style="{ width: duringProgress + '%' }"
            ></div>
          </div>
        </div>
        <div class="target-card">
          <span class="target-label">Post-Match</span>
          <span class="target-value">{{ targets.post_match_ml }}ml</span>
          <div class="target-bar">
            <div
              class="target-fill"
              :style="{ width: postMatchProgress + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <div class="schedule-section">
      <h4>Today's Schedule</h4>
      <div class="schedule-timeline">
        <div
          v-for="(item, index) in schedule"
          :key="item.time"
          class="schedule-item"
          :class="{
            completed: isTimeCompleted(item.time, item.reason),
            current: !isTimeCompleted(item.time, item.reason) && !isUpcoming(item.time),
            upcoming: isUpcoming(item.time),
            selected: selectedScheduleIndex === index
          }"
        >
          <div class="schedule-time-block">
            <span class="schedule-time">{{ formatTime(item.time) }}</span>
          </div>
          <div class="schedule-connector">
            <div class="connector-dot" :class="{ filled: isTimeCompleted(item.time, item.reason) }">
              <span v-if="isTimeCompleted(item.time, item.reason)" class="check-mark">&#10003;</span>
            </div>
            <div v-if="index < schedule.length - 1" class="connector-line"></div>
          </div>
          <div
            class="schedule-card"
            :class="{ clickable: !isTimeCompleted(item.time, item.reason) && !isUpcoming(item.time) }"
            @click="onScheduleCardClick(item, index)"
          >
            <!-- Completed state: show what was logged -->
            <template v-if="isTimeCompleted(item.time, item.reason)">
              <div class="schedule-card-header">
                <span class="schedule-amount">{{ getLoggedAmount(item.time, item.reason) }}ml</span>
                <span class="schedule-type-badge" :class="getLoggedDrinkType(item.time, item.reason)">
                  {{ formatDrinkType(getLoggedDrinkType(item.time, item.reason)) }}
                </span>
              </div>
              <span class="schedule-reason completed-text">{{ item.reason }}</span>
            </template>

            <!-- Not completed: show target -->
            <template v-else>
              <div class="schedule-card-header">
                <span class="schedule-amount">{{ item.amount }}ml</span>
                <span class="schedule-type-badge" :class="item.type">{{ item.type }}</span>
              </div>
              <span class="schedule-reason">{{ item.reason }}</span>
              <span v-if="!isUpcoming(item.time)" class="tap-hint">Tap to log</span>
            </template>

            <!-- Inline quick add panel -->
            <div v-if="selectedScheduleIndex === index" class="inline-quick-add" @click.stop>
              <div class="inline-drink-types">
                <button
                  v-for="drink in drinkTypes"
                  :key="drink.value"
                  class="inline-drink-btn"
                  :class="{ active: selectedDrink === drink.value }"
                  @click="selectedDrink = drink.value"
                >
                  <span class="drink-icon-small" :style="{ backgroundColor: drink.color }">
                    {{ drink.symbol }}
                  </span>
                  {{ drink.label }}
                </button>
              </div>
              <div class="inline-amounts">
                <button
                  v-for="portion in quickPortions"
                  :key="portion.ml"
                  class="inline-amount-btn"
                  @click="addToScheduleSlot(portion.ml)"
                  :disabled="addingHydration"
                >
                  {{ portion.ml }}ml
                </button>
              </div>
              <button class="cancel-btn" @click="selectedScheduleIndex = null">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="logs.length" class="recent-logs">
      <h4>Recent Intake</h4>
      <div class="logs-list">
        <div v-for="log in recentLogs" :key="log.id + '-' + log.source" class="log-item" :class="{ 'from-meal': log.source && log.source !== 'hydration' }">
          <span class="log-time">{{ formatTime(log.log_time) }}</span>
          <span class="log-amount">{{ log.amount_ml }}ml</span>
          <span v-if="log.source && log.source !== 'hydration'" class="log-type meal-source">
            with {{ log.source }}
          </span>
          <span v-else class="log-type">{{ formatDrinkType(log.drink_type) }}</span>
          <button
            v-if="!log.source || log.source === 'hydration'"
            class="delete-log-btn"
            @click="deleteLog(log)"
            :disabled="deletingLog === log.id"
            title="Remove"
          >
            &#10005;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Nutrition from "@/services/nutrition-service";

export default {
  name: "HydrationTracker",

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    teamId: {
      type: [Number, String],
      default: null,
    },
    date: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
    weight: {
      type: Number,
      default: 70,
    },
  },

  data() {
    return {
      loading: false,
      addingHydration: false,
      deletingLog: null,
      currentIntake: 0,
      percentage: 0,
      dailyTarget: 2450, // Default: 70kg * 35ml
      targets: {
        daily_ml: 2450,
        pre_match_ml: 0,
        during_ml: 0,
        post_match_ml: 0,
      },
      schedule: [],
      logs: [],
      activity: null,
      selectedDrink: "water",
      quickPortions: [],
      drinkTypes: [],
      selectedScheduleIndex: null,
    };
  },

  computed: {
    circumference() {
      return 2 * Math.PI * 45;
    },

    progressOffset() {
      return this.circumference - (this.percentage / 100) * this.circumference;
    },

    progressColor() {
      if (this.percentage >= 100) return "#28a745";
      if (this.percentage >= 75) return "#20c997";
      if (this.percentage >= 50) return "#17a2b8";
      if (this.percentage >= 25) return "#ffc107";
      return "#dc3545";
    },

    isMatchDay() {
      return this.activity && this.activity.type === "match";
    },

    behindSchedule() {
      const hour = new Date().getHours();
      const expectedPercentage = Math.min(100, ((hour - 7) / 13) * 100);
      return this.percentage < expectedPercentage - 15;
    },

    preMatchProgress() {
      return this.targets.pre_match_ml > 0
        ? Math.min(100, (this.calculatePeriodIntake("pre") / this.targets.pre_match_ml) * 100)
        : 0;
    },

    duringProgress() {
      return this.targets.during_ml > 0
        ? Math.min(100, (this.calculatePeriodIntake("during") / this.targets.during_ml) * 100)
        : 0;
    },

    postMatchProgress() {
      return this.targets.post_match_ml > 0
        ? Math.min(100, (this.calculatePeriodIntake("post") / this.targets.post_match_ml) * 100)
        : 0;
    },

    recentLogs() {
      return this.logs.slice(-5).reverse();
    },
  },

  async mounted() {
    this.quickPortions = Nutrition.getQuickPortions();
    this.drinkTypes = Nutrition.getDrinkTypes();
    await this.loadHydrationPlan();
  },

  methods: {
    async loadHydrationPlan() {
      this.loading = true;
      try {
        const response = await Nutrition.getHydrationPlan(this.userId, this.date, {
          teamId: this.teamId,
          weight: this.weight,
        });

        if (response.data && response.data.success) {
          const data = response.data.data;
          this.activity = data.activity;
          this.targets = data.targets || this.targets;
          this.dailyTarget = this.targets.daily_ml || 2450;
          this.schedule = data.schedule || [];
          this.logs = data.logs || [];
          this.currentIntake = data.current_intake || 0;
          this.percentage = data.percentage || 0;
        }
      } catch (error) {
        console.error("Error loading hydration plan:", error);
      } finally {
        this.loading = false;
      }
    },

    async quickAdd(amount) {
      this.addingHydration = true;
      try {
        const response = await Nutrition.logHydration({
          user_id: this.userId,
          amount_ml: amount,
          drink_type: this.selectedDrink,
          log_date: this.date,
        });

        if (response.data && response.data.success) {
          this.currentIntake += amount;
          this.percentage = Math.min(100, Math.round((this.currentIntake / this.dailyTarget) * 100));

          // Add to logs - use spread to trigger reactivity
          const newLog = {
            id: response.data.id,
            log_time: new Date().toTimeString().slice(0, 5),
            amount_ml: amount,
            drink_type: this.selectedDrink,
            source: 'hydration'
          };
          this.logs = [...this.logs, newLog];

          this.$emit("hydration-logged", { amount, type: this.selectedDrink });
        }
      } catch (error) {
        console.error("Error logging hydration:", error);
      } finally {
        this.addingHydration = false;
      }
    },

    async deleteLog(log) {
      if (!confirm("Remove this entry?")) return;

      this.deletingLog = log.id;
      try {
        const response = await Nutrition.deleteHydration(log.id, this.userId);

        if (response.data && response.data.success) {
          // Update totals
          this.currentIntake -= log.amount_ml;
          this.percentage = Math.max(0, Math.round((this.currentIntake / this.dailyTarget) * 100));

          // Remove from logs - create new array to trigger reactivity
          this.logs = [...this.logs.filter((l) => l.id !== log.id)];

          // Force schedule to re-evaluate
          this.$forceUpdate();

          this.$emit("hydration-deleted", { amount: log.amount_ml });
        }
      } catch (error) {
        console.error("Error deleting hydration log:", error);
      } finally {
        this.deletingLog = null;
      }
    },

    formatTime(time) {
      if (!time) return "";
      const [hours, mins] = time.split(":");
      const h = parseInt(hours, 10);
      const period = h >= 12 ? "PM" : "AM";
      const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return `${displayHour}:${mins} ${period}`;
    },

    formatDrinkType(type) {
      const drink = this.drinkTypes.find((d) => d.value === type);
      return drink ? drink.label : type;
    },

    isTimeCompleted(time, reason) {
      const now = new Date();
      const [hours, mins] = time.split(":");
      const scheduleTime = new Date();
      scheduleTime.setHours(parseInt(hours, 10), parseInt(mins, 10), 0);

      // Not yet time for this slot
      if (now < scheduleTime) return false;

      // Check if we have a log around this time or matching source
      const hasLog = this.logs.some((log) => {
        const logTime = (log.log_time || "00:00").split(":")[0];
        const hourDiff = Math.abs(parseInt(logTime, 10) - parseInt(hours, 10));

        // Check for meal-based hydration (source will be meal type like 'lunch', 'dinner')
        if (log.source && log.source !== 'hydration') {
          // If reason mentions the meal type, it's a match
          const reasonLower = (reason || "").toLowerCase();
          const sourceLower = log.source.toLowerCase();
          if (reasonLower.includes(sourceLower) || reasonLower.includes("lunch") && sourceLower === "lunch") {
            return true;
          }
        }

        // Time-based match (within 1 hour)
        return hourDiff <= 1;
      });

      return hasLog;
    },

    isCurrentSlot(time, index) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMins = now.getMinutes();
      const [hours, mins] = time.split(":");
      const slotHour = parseInt(hours, 10);

      // Check if this is the current or next upcoming slot
      if (index < this.schedule.length - 1) {
        const nextTime = this.schedule[index + 1].time;
        const [nextHours] = nextTime.split(":");
        const nextHour = parseInt(nextHours, 10);

        // Current slot if we're between this time and next time
        if (currentHour >= slotHour && currentHour < nextHour) {
          return true;
        }
      } else {
        // Last slot - current if we're past this time
        if (currentHour >= slotHour) {
          return true;
        }
      }

      return false;
    },

    calculatePeriodIntake(period) {
      // Simplified - in real implementation would calculate based on match time
      if (period === "pre") {
        return Math.min(this.currentIntake, this.targets.pre_match_ml);
      }
      return 0;
    },

    isUpcoming(time) {
      // Only upcoming if the scheduled time is in the future
      const now = new Date();
      const [hours, mins] = time.split(":");
      const scheduleTime = new Date();
      scheduleTime.setHours(parseInt(hours, 10), parseInt(mins || 0, 10), 0);
      return now < scheduleTime;
    },

    onScheduleCardClick(item, index) {
      // Don't allow clicking completed or upcoming slots
      if (this.isTimeCompleted(item.time, item.reason)) return;
      if (this.isUpcoming(item.time)) return;

      // Toggle selection
      if (this.selectedScheduleIndex === index) {
        this.selectedScheduleIndex = null;
      } else {
        this.selectedScheduleIndex = index;
      }
    },

    getLogForScheduleItem(time, reason) {
      const [hours] = time.split(":");
      const scheduleHour = parseInt(hours, 10);

      // Find matching log
      return this.logs.find((log) => {
        const logTime = (log.log_time || "00:00").split(":")[0];
        const logHour = parseInt(logTime, 10);
        const hourDiff = Math.abs(logHour - scheduleHour);

        // Check for meal-based hydration first
        if (log.source && log.source !== 'hydration') {
          const reasonLower = (reason || "").toLowerCase();
          const sourceLower = log.source.toLowerCase();
          if (reasonLower.includes(sourceLower)) {
            return true;
          }
        }

        // Time-based match
        return hourDiff <= 1;
      });
    },

    getLoggedAmount(time, reason) {
      const log = this.getLogForScheduleItem(time, reason);
      return log ? log.amount_ml : 0;
    },

    getLoggedDrinkType(time, reason) {
      const log = this.getLogForScheduleItem(time, reason);
      return log ? log.drink_type : "water";
    },

    async addToScheduleSlot(amount) {
      const scheduleItem = this.schedule[this.selectedScheduleIndex];
      if (!scheduleItem) return;

      this.addingHydration = true;
      try {
        const response = await Nutrition.logHydration({
          user_id: this.userId,
          amount_ml: amount,
          drink_type: this.selectedDrink,
          log_date: this.date,
          log_time: scheduleItem.time, // Use the scheduled time
        });

        if (response.data && response.data.success) {
          this.currentIntake += amount;
          this.percentage = Math.min(100, Math.round((this.currentIntake / this.dailyTarget) * 100));

          // Add to logs - use the scheduled time so it matches
          const newLog = {
            id: response.data.id,
            log_time: scheduleItem.time,
            amount_ml: amount,
            drink_type: this.selectedDrink,
            source: 'hydration'
          };
          this.logs = [...this.logs, newLog];

          // Close the inline quick add panel
          this.selectedScheduleIndex = null;

          this.$emit("hydration-logged", { amount, type: this.selectedDrink });
        }
      } catch (error) {
        console.error("Error logging hydration:", error);
      } finally {
        this.addingHydration = false;
      }
    },
  },

  watch: {
    date() {
      this.loadHydrationPlan();
    },
  },
};
</script>

<style lang="scss" scoped>
.hydration-tracker {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tracker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.match-day-badge {
  background: #dc3545;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
}

.progress-section {
  text-align: center;
  margin-bottom: 24px;
}

.circular-progress {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto 12px;

  svg {
    transform: rotate(-90deg);
    width: 100%;
    height: 100%;
  }

  .progress-fill {
    transition: stroke-dashoffset 0.5s ease;
  }
}

.progress-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-value {
  display: block;
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
}

.progress-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #17a2b8;
}

.progress-target {
  display: block;
  font-size: 0.75rem;
  color: #888;
}

.behind-alert {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff3cd;
  color: #856404;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
}

.alert-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #ffc107;
  color: #333;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.75rem;
}

.quick-add-section,
.drink-type-section,
.schedule-section,
.match-day-targets,
.recent-logs {
  margin-bottom: 20px;

  h4 {
    font-size: 0.85rem;
    font-weight: 600;
    color: #555;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.quick-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-btn {
  flex: 1;
  min-width: 70px;
  padding: 10px 8px;
  border: 1px solid #17a2b8;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e7f7fa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.portion-amount {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #17a2b8;
}

.portion-label {
  display: block;
  font-size: 0.65rem;
  color: #888;
}

.drink-types {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.drink-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #17a2b8;
  }

  &.active {
    border-color: #17a2b8;
    background: #e7f7fa;
  }
}

.drink-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: white;
}

.drink-label {
  font-size: 0.8rem;
  color: #333;
}

.target-cards {
  display: flex;
  gap: 10px;
}

.target-card {
  flex: 1;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 10px;
  text-align: center;
}

.target-label {
  display: block;
  font-size: 0.7rem;
  color: #666;
  margin-bottom: 4px;
}

.target-value {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.target-bar {
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
}

.target-fill {
  height: 100%;
  background: #17a2b8;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.schedule-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.schedule-item {
  display: flex;
  align-items: stretch;
  min-height: 70px;
  transition: all 0.2s ease;

  &.completed {
    .schedule-card {
      background: #f0fdf4;
      border-color: #86efac;
    }

    .schedule-time {
      color: #22c55e;
    }

    .schedule-amount {
      color: #16a34a;
    }
  }

  &.current {
    .schedule-card {
      background: #eff6ff;
      border-color: #60a5fa;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
    }

    .connector-dot {
      background: #3b82f6;
      border-color: #3b82f6;
      box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
    }

    .schedule-time {
      color: #3b82f6;
      font-weight: 700;
    }
  }

  &.upcoming {
    .schedule-card {
      background: #fafafa;
      border-color: #e5e5e5;
    }

    .schedule-time {
      color: #a3a3a3;
    }

    .connector-dot {
      background: white;
    }
  }
}

.schedule-time-block {
  width: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding-top: 12px;
}

.schedule-time {
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
  text-align: right;
  width: 100%;
}

.schedule-connector {
  width: 32px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.connector-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #17a2b8;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  margin-top: 10px;
  flex-shrink: 0;

  &.filled {
    background: #22c55e;
    border-color: #22c55e;
  }
}

.check-mark {
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
}

.connector-line {
  width: 2px;
  flex-grow: 1;
  background: linear-gradient(to bottom, #17a2b8, #e5e5e5);
  margin-top: 4px;
}

.schedule-card {
  flex: 1;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 4px 0;
  transition: all 0.2s ease;
}

.schedule-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.schedule-amount {
  font-size: 1rem;
  font-weight: 700;
  color: #17a2b8;
}

.schedule-type-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: capitalize;

  &.water {
    background: #e0f2fe;
    color: #0284c7;
  }

  &.electrolytes {
    background: #fef3c7;
    color: #d97706;
  }

  &.sports_drink {
    background: #fce7f3;
    color: #db2777;
  }
}

.schedule-reason {
  font-size: 0.8rem;
  color: #666;
  line-height: 1.3;
}

.logs-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.log-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.log-time {
  font-size: 0.75rem;
  color: #888;
}

.log-amount {
  font-size: 0.85rem;
  font-weight: 600;
  color: #17a2b8;
}

.log-type {
  font-size: 0.75rem;
  color: #666;
  margin-left: auto;
}

.delete-log-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #999;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #f8d7da;
    color: #dc3545;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Clickable schedule card styles
.schedule-card.clickable {
  cursor: pointer;

  &:hover {
    border-color: #17a2b8;
    background: #f0fafc;
  }
}

.schedule-item.selected {
  .schedule-card {
    border-color: #17a2b8;
    background: #e7f7fa;
    box-shadow: 0 2px 12px rgba(23, 162, 184, 0.2);
  }
}

.tap-hint {
  display: block;
  font-size: 0.7rem;
  color: #17a2b8;
  margin-top: 4px;
  font-style: italic;
}

.completed-text {
  color: #22c55e;
}

// Inline quick add panel
.inline-quick-add {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.inline-drink-types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.inline-drink-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #ddd;
  border-radius: 16px;
  background: white;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #17a2b8;
  }

  &.active {
    border-color: #17a2b8;
    background: #17a2b8;
    color: white;

    .drink-icon-small {
      border: 1px solid white;
    }
  }
}

.drink-icon-small {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 700;
  color: white;
}

.inline-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.inline-amount-btn {
  padding: 6px 12px;
  border: 1px solid #17a2b8;
  border-radius: 6px;
  background: white;
  color: #17a2b8;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #17a2b8;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.cancel-btn {
  padding: 6px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #f8f9fa;
  color: #666;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
  }
}
</style>
